import React, { useState, useEffect } from 'react';
import { X, Video, Globe, MousePointer, RotateCcw, Monitor, Volume2, AlertTriangle, Camera, RefreshCw, Network } from 'lucide-react';
import type { AppConfig, IcePolicy, StatusBarVisibility, StatusBarOrientation, TurnProtocol, ServerStats, InternalTurnStatus, InternalTurnEndpoint, EncoderCatalogEntry, EncoderId } from '../types';
import { DEFAULT_CONFIG } from '../constants';
import { eventBus } from '../core/EventBus';
import { webRTCService } from '../core/WebRTCService';
import { signalingService } from '../core/SignalingService';
import { statsService } from '../core/StatsService';

interface CapturerBenchOne {
  tested: boolean;
  available: boolean;
  successCount: number;
  totalCount: number;
  avgMs: number;
  frameWidth: number;
  frameHeight: number;
  note: string;
}
type CapturerKind = 'auto' | 'wgc' | 'dxgi' | 'gdi';
interface ScreenCapturerStatus {
  active: CapturerKind;
  preferred: CapturerKind;
  userOverride: boolean;
  supportedOnThisOs: CapturerKind[];
  lastBenchmark: null | {
    os: string;
    displaySignature: string;
    wgc: CapturerBenchOne;
    dxgi: CapturerBenchOne;
    gdi: CapturerBenchOne;
    winner: CapturerKind;
    ts: number;
  };
}

interface SettingsPanelProps {
  isOpen: boolean;
  config: AppConfig;
  onConfigChange: (config: AppConfig) => void;
  onClose: () => void;
  hostPlatform?: string;  // "w7" | "w10" | ""
  availableResolutions?: string;  // "1920x1080,1280x720,..."
  currentResolution?: string;     // "1920x1080"
  encoderName?: string;           // "NVENC H.264" | "x264 (Software)" | ...
  viewOnly?: boolean;             // 只读模式下隐藏会向远端发指令的设置项（远程分辨率切换）
  screenCapturer?: ScreenCapturerStatus | null;  // host 推送的截图器状态（App 顶层维护）
}

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ icon, title, children }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
      {icon} {title}
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

interface ToggleProps {
  label: string;
  description?: string;
  active: boolean;
  onToggle: () => void;
  disabled?: boolean;
  disabledReason?: string;
}

const Toggle: React.FC<ToggleProps> = ({ label, description, active, onToggle, disabled, disabledReason }) => (
  <div className={`flex justify-between items-center group ${disabled ? 'opacity-40' : ''}`} title={disabled ? disabledReason : undefined}>
    <div className="flex-1">
      <span className="text-sm text-zinc-300 group-hover:text-white transition-colors">{label}</span>
      {description && <p className="text-[10px] text-zinc-600 mt-0.5">{disabled ? disabledReason : description}</p>}
    </div>
    <button
      onClick={() => !disabled && onToggle()}
      className={`relative w-10 h-5 rounded-full transition-colors duration-300 ${disabled ? 'bg-zinc-800 cursor-not-allowed' : active ? 'bg-blue-600' : 'bg-zinc-700'}`}
    >
      <div
        className={`absolute top-1 left-1 w-3 h-3 bg-white rounded-full transition-transform duration-300 ${active && !disabled ? 'translate-x-5' : 'translate-x-0'}`}
      />
    </button>
  </div>
);

interface SelectProps {
  label: string;
  value: string;
  options: Array<{ value: string; label: string; disabled?: boolean; disabledReason?: string }>;
  onChange: (value: string) => void;
}

const Select: React.FC<SelectProps> = ({ label, value, options, onChange }) => (
  <div className="space-y-2">
    <div className="text-xs text-zinc-500">{label}</div>
    <div className="flex bg-zinc-800 p-1 rounded-lg gap-1">
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => !opt.disabled && onChange(opt.value)}
          title={opt.disabled ? opt.disabledReason : undefined}
          className={`flex-1 py-1.5 px-2 rounded-md text-[10px] uppercase font-bold tracking-tight transition-all ${
            opt.disabled
              ? 'text-zinc-700 cursor-not-allowed line-through'
              : value === opt.value
                ? 'bg-zinc-600 text-white'
                : 'text-zinc-500 hover:text-zinc-300'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);

interface RangeProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit: string;
  onChange: (value: number) => void;
  onChangeEnd?: (value: number) => void;  // 鼠标放开时触发
  disabled?: boolean;
  labelExtra?: React.ReactNode;
  hint?: string;  // 提示文字
}

const Range: React.FC<RangeProps> = ({ label, value, min, max, step = 1, unit, onChange, onChangeEnd, disabled, labelExtra, hint }) => {
  const [localValue, setLocalValue] = useState(value);

  // 同步外部value变化
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return (
    <div className={`space-y-3 ${disabled ? 'opacity-50' : ''}`}>
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500">{label}</span>
          {labelExtra}
        </div>
        <div className="flex items-center gap-2">
          {hint && <span className="text-[10px] text-yellow-500">{hint}</span>}
          <span className="text-xs font-mono text-blue-400">
            {localValue} {unit}
          </span>
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={localValue}
        onChange={(e) => {
          const v = parseInt(e.target.value);
          setLocalValue(v);
          onChange(v);  // 实时更新本地状态
        }}
        onMouseUp={() => onChangeEnd?.(localValue)}
        onTouchEnd={() => onChangeEnd?.(localValue)}
        className="w-full"
        disabled={disabled}
      />
    </div>
  );
};

// ===========================================================================
// EncoderCatalogSection（阶段 2）
// 数据源：serverStats.encoder_catalog + active_encoder_id + probe_pending
// （来自 host server_encoder_catalog 信令，由 StatsService 合并到 ServerStats）
//
// 行为：
//   - probe_pending 或无 catalog → 显示"加载中"占位
//   - 渲染下拉按钮：'自动' + 全部 available + 全部 unavailable (disabled + tooltip 显示原因)
//   - 全部 unavailable → 顶部显示红色横幅"主机所有编码器均不可用"
//   - 切换：发 select_encoder 信令；不弹确认，下方常驻提示文字
//   - 右上角"重新检测"按钮：发 probe_rerun 信令
// ===========================================================================

// reason_code → 中文文案
function reasonText(code: string): string {
  switch (code) {
    case 'no_dll':              return 'DLL 未安装';
    case 'init_failed':         return '初始化失败';
    case 'session_failed':      return 'GPU 会话创建失败（驱动可能不兼容）';
    case 'codec_not_supported': return 'GPU 不支持此编码器';
    case 'encode_failed':       return '编码测试失败';
    case 'timeout':             return '自检超时';
    case 'cores_too_few':       return 'CPU 核心数不足';
    case 'disabled_by_env':     return '已被环境变量禁用';
    case 'probe_pending':       return '自检进行中';
    case 'runtime_fallback':    return '运行期已降级';
    case 'slow_perf_warn':      return '性能不足';
    default:                    return code || '不可用';
  }
}

// 编码器显示名（id → "NVENC AV1 (GPU)" / "x264 (CPU)" 等）
function encoderDisplayName(id: EncoderId): string {
  switch (id) {
    case 'nvenc_av1':     return 'NVENC AV1';
    case 'nvenc_h265':    return 'NVENC H.265';
    case 'nvenc_h264':    return 'NVENC H.264';
    case 'libaom_av1':    return 'AV1 (libaom)';
    case 'x264_h264':     return 'x264';
    case 'openh264_h264': return 'OpenH264';
    default:              return id;
  }
}

interface EncoderCatalogSectionProps {
  serverStats: ServerStats | null;
}

const EncoderCatalogSection: React.FC<EncoderCatalogSectionProps> = ({ serverStats }) => {
  const catalog: EncoderCatalogEntry[] | undefined = serverStats?.encoder_catalog;
  const activeId = serverStats?.active_encoder_id;
  const probePending = serverStats?.probe_pending;
  const [reprobeBusy, setReprobeBusy] = useState(false);
  const [reprobeMsg, setReprobeMsg] = useState<string>('');
  // 点击"重新检测"时记录时间戳，用于：
  // 1) catalog 中 last_probe_at 比此时间戳新 → 视为 probe 完成（即便 ack 在网络抖动中丢失也能恢复）
  // 2) 超时兜底（10 秒）自动清状态
  const reprobeStartRef = React.useRef<number>(0);

  // 订阅 host ack（probe_rerun_ack / select_encoder_ack）以更新提示
  useEffect(() => {
    const onProbeAck = (data: { ok: boolean; reason_code?: string }) => {
      setReprobeBusy(false);
      reprobeStartRef.current = 0;
      if (!data.ok && data.reason_code === 'already_running') {
        setReprobeMsg('已在检测中，请稍后');
      } else if (data.ok) {
        setReprobeMsg('');
      }
    };
    const onSelectAck = (data: { ok: boolean; reason_code?: string; active_encoder_id?: string }) => {
      if (!data.ok) {
        eventBus.emit('ui:toast', {
          message: `编码器切换失败：${reasonText(data.reason_code || '')}`,
          type: 'error',
        });
        return;
      }
      eventBus.emit('ui:toast', {
        message: `已切换到 ${encoderDisplayName(data.active_encoder_id as EncoderId)}，正在重连...`,
        type: 'info',
      });
      // 主动重连（前端驱动）— 走 reconnectWithNewConfig 快速路径，约 500ms 后开始新连接
      // 不再等 host 关 session，避免 host 关 → 前端被动 close → 重连的多余 round-trip（旧逻辑就是这么做的）
      webRTCService.reconnectWithNewConfig({});
    };
    eventBus.on('encoder:probeRerunAck', onProbeAck);
    eventBus.on('encoder:selectAck', onSelectAck);
    return () => {
      eventBus.off('encoder:probeRerunAck', onProbeAck);
      eventBus.off('encoder:selectAck', onSelectAck);
    };
  }, []);

  // 备用恢复机制：catalog 中 last_probe_at 比"点击时间戳"新 → probe 已完成（即便 ack 丢失）
  useEffect(() => {
    if (!reprobeBusy || !catalog || reprobeStartRef.current === 0) return;
    const newest = catalog.reduce((m, e) => Math.max(m, e.last_probe_at || 0), 0);
    if (newest * 1000 > reprobeStartRef.current) {
      setReprobeBusy(false);
      setReprobeMsg('');
      reprobeStartRef.current = 0;
    }
  }, [catalog, reprobeBusy]);

  // 超时兜底：10 秒后强制清状态，UI 不会永久卡在"检测中"
  useEffect(() => {
    if (!reprobeBusy) return;
    const timer = window.setTimeout(() => {
      setReprobeBusy(false);
      setReprobeMsg('（检测超时，请重试）');
      reprobeStartRef.current = 0;
    }, 10000);
    return () => clearTimeout(timer);
  }, [reprobeBusy]);

  // 还没收到 catalog
  if (!catalog || catalog.length === 0) {
    return (
      <div className="space-y-2">
        <div className="text-xs text-zinc-500">视频编码器</div>
        <div className="bg-zinc-800 p-2 rounded-lg text-[10px] text-zinc-500 italic">
          加载服务端能力中...
        </div>
      </div>
    );
  }

  const availables = catalog.filter((e) => e.available);
  const unavailables = catalog.filter((e) => !e.available);
  const allUnavailable = availables.length === 0 && !probePending;

  const handleReprobe = () => {
    if (reprobeBusy) return;
    setReprobeBusy(true);
    setReprobeMsg('正在重新检测编码器...');
    reprobeStartRef.current = Date.now();
    webRTCService.sendProbeReRun();
  };

  const handleSelect = (id: EncoderId | 'auto') => {
    if (id === 'auto') {
      // 自动：按 priority 升序选第一个 available
      const sorted = [...availables].sort((a, b) => a.priority - b.priority);
      if (sorted.length === 0) return;
      webRTCService.sendSelectEncoder(sorted[0].id);
    } else {
      webRTCService.sendSelectEncoder(id);
    }
  };

  // 当前 active 显示文案
  const activeLabel = activeId && activeId !== 'none'
    ? encoderDisplayName(activeId as EncoderId)
    : '未选定';

  return (
    <div className="space-y-2">
      {/* 标题行 + 重新检测按钮 */}
      <div className="flex justify-between items-center">
        <div className="text-xs text-zinc-500">视频编码器</div>
        <button
          onClick={handleReprobe}
          disabled={reprobeBusy}
          className={`text-[10px] px-2 py-0.5 rounded transition-colors ${
            reprobeBusy
              ? 'text-zinc-600 cursor-not-allowed'
              : 'text-blue-400 hover:text-blue-300 hover:bg-zinc-800'
          }`}
          title="强制重新检测所有编码器，结果会写入缓存文件"
        >
          {reprobeBusy ? '检测中...' : '重新检测'}
        </button>
      </div>

      {/* 全部不可用时的红色横幅 */}
      {allUnavailable && (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 px-2 py-1.5 text-[11px] text-red-300 leading-relaxed">
          ⚠️ 主机所有编码器均不可用，视频流已停止。可点击「重新检测」尝试恢复，
          或重启主机；远程控制 / 文件传输等非视频功能仍可继续使用。
        </div>
      )}

      {/* 自检中提示 */}
      {probePending && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-300">
          ⏳ 主机正在自检编码器，约 1~2 秒后选项将刷新
        </div>
      )}

      {/* 可用编码器按钮组 + 自动选项 */}
      <div className="flex flex-wrap gap-1 bg-zinc-800 p-1 rounded-lg">
        {/* 自动 */}
        {availables.length > 1 && (
          <button
            onClick={() => handleSelect('auto')}
            className="px-2 py-1.5 rounded-md text-[10px] uppercase font-bold tracking-tight text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700 transition-colors"
            title="按优先级自动选择最佳可用编码器"
          >
            自动
          </button>
        )}
        {availables.map((e) => {
          const selected = activeId === e.id;
          return (
            <button
              key={e.id}
              onClick={() => handleSelect(e.id)}
              className={`px-2 py-1.5 rounded-md text-[10px] uppercase font-bold tracking-tight transition-colors ${
                selected
                  ? 'bg-blue-600 text-white'
                  : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'
              }`}
              title={`${encoderDisplayName(e.id)} (${e.accel}) — init=${e.probe_init_ms}ms, encode=${e.probe_encode_ms}ms`}
            >
              {encoderDisplayName(e.id)}
              <span className="ml-1 text-[9px] opacity-60">{e.accel}</span>
              {e.slow_perf_warn && <span className="ml-1 text-amber-300">⚠</span>}
            </button>
          );
        })}
      </div>

      {/* 不可用编码器（灰色 disabled） */}
      {unavailables.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {unavailables.map((e) => (
            <span
              key={e.id}
              className="px-2 py-1 rounded text-[10px] uppercase font-bold tracking-tight text-zinc-600 bg-zinc-900/50 cursor-not-allowed line-through"
              title={`${encoderDisplayName(e.id)} 不可用：${reasonText(e.reason_code)}`}
            >
              {encoderDisplayName(e.id)}
            </span>
          ))}
        </div>
      )}

      {/* 当前激活 + 切换说明 */}
      <div className="text-[10px] text-zinc-500 leading-relaxed">
        当前激活：<span className="text-zinc-300">{activeLabel}</span>
        {reprobeMsg && <span className="ml-2 text-amber-400">{reprobeMsg}</span>}
        <br />
        切换编码器会立即断开当前连接并自动重连。
      </div>
    </div>
  );
};

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  isOpen,
  config,
  onConfigChange,
  onClose,
  hostPlatform,
  availableResolutions,
  currentResolution,
  encoderName,
  viewOnly = false,
  screenCapturer = null,
}) => {
  const isWin7 = hostPlatform === 'w7';

  // 订阅 server_stats，避免组件挂载后到达的更新不触发重渲染
  // （statsService.serverStats 是普通对象属性，不是 React state）
  const [serverStats, setServerStats] = useState<ServerStats | null>(statsService.serverStats);
  useEffect(() => {
    console.log('[SettingsPanel] subscribe stats:serverStats, current=', statsService.serverStats);
    const unsub = eventBus.on('stats:serverStats', (s: ServerStats) => {
      setServerStats(s);
    });
    // 订阅前 stats 可能已经到达，立即取一次现值
    if (statsService.serverStats) {
      setServerStats(statsService.serverStats);
    }
    return () => unsub();
  }, []);

  // 软/硬编判定：优先用 catalog 的 active entry.accel；兜底用旧 encoder_type；最后正则 fallback
  const isSoftwareEncoder = (() => {
    if (serverStats?.encoder_catalog && serverStats.active_encoder_id) {
      const e = serverStats.encoder_catalog.find(x => x.id === serverStats.active_encoder_id);
      if (e) return e.accel !== 'GPU';
    }
    if (serverStats?.encoder_type) {
      return serverStats.encoder_type !== 'NVENC';
    }
    return /x264|openh264|cpu|software/i.test(encoderName || '');
  })();

  // Local config state
  const [localConfig, setLocalConfig] = useState<AppConfig>({ ...config });

  // Sync localConfig when panel opens or config changes externally
  useEffect(() => {
    if (isOpen) {
      setLocalConfig({ ...config });
    }
  }, [isOpen, config]);

  // ===== 截图器：状态由 App 顶层维护（避免后开面板错过初次广播）=====
  const [rebenchPending, setRebenchPending] = useState(false);
  // 收到新的 screenCapturer 推送（包括 ts 变化）即视为重测完成
  useEffect(() => {
    setRebenchPending(false);
  }, [screenCapturer?.lastBenchmark?.ts]);

  // ===== 内网 TURN 状态 =====
  const [turnStatus, setTurnStatus] = useState<InternalTurnStatus | null>(null);
  const [turnProbingAll, setTurnProbingAll] = useState(false);
  const [turnProbingKey, setTurnProbingKey] = useState<string | null>(null);  // 单条测速时为 "ip:port"
  const [turnRediscovering, setTurnRediscovering] = useState(false);
  useEffect(() => {
    const unsub = eventBus.on('internal_turn:status', (s) => {
      setTurnStatus(s as InternalTurnStatus);
      setTurnProbingAll(false);
      setTurnProbingKey(null);
      setTurnRediscovering(false);
    });
    return () => unsub();
  }, []);
  useEffect(() => {
    if (isOpen) webRTCService.queryInternalTurn();
  }, [isOpen]);


  // Update config - all settings apply immediately
  const updateConfig = <K extends keyof AppConfig>(key: K, value: AppConfig[K]) => {
    const newConfig = { ...localConfig, [key]: value };
    setLocalConfig(newConfig);
    onConfigChange(newConfig);
  };

  const resetToDefaults = () => {
    setLocalConfig({ ...DEFAULT_CONFIG });
    onConfigChange({ ...DEFAULT_CONFIG });
  };


  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 z-[80] transition-opacity duration-500 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed top-4 right-4 bottom-4 w-80 glass-high z-[90] rounded-3xl shadow-4xl transform transition-transform duration-500 ease-apple overflow-hidden flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-[110%]'
        }`}
      >
        {/* Header */}
        <div className="p-6 border-b border-white/5 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-white">会话配置</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
            <X size={20} className="text-zinc-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          {/* Network Settings */}
          <Section icon={<Globe size={16} />} title="网络设置">
            <Select
              label="ICE 连接策略"
              value={localConfig.icePolicy}
              options={[
                { value: 'auto', label: '自动' },
                { value: 'p2p', label: '仅 P2P' },
                { value: 'relay', label: '仅中转' },
              ]}
              onChange={(v) => {
                const newPolicy = v as IcePolicy;
                updateConfig('icePolicy', newPolicy);
                // ICE策略需要重连才能生效
                eventBus.emit('ui:toast', { message: 'ICE策略已更新，正在重连...', type: 'info' });
                // 触发重连
                webRTCService.reconnectWithNewConfig({ icePolicy: newPolicy });
              }}
            />
            <div className="text-[10px] text-zinc-500 -mt-2">
              自动：优先P2P，丢包5秒后自动切换中转
            </div>
            {(localConfig.icePolicy === 'auto' || localConfig.icePolicy === 'relay') && (
              <>
                <Select
                  label="TURN 协议"
                  value={localConfig.turnProtocol || 'auto'}
                  options={[
                    { value: 'auto', label: '自动' },
                    { value: 'udp', label: 'UDP' },
                    { value: 'tcp', label: 'TCP' },
                  ]}
                  onChange={(v) => {
                    const newProtocol = v as TurnProtocol;
                    // TCP 中转只在 relay 模式下有意义，自动联动切换
                    if (newProtocol === 'tcp' && localConfig.icePolicy !== 'relay') {
                      const newConfig = { ...localConfig, turnProtocol: newProtocol, icePolicy: 'relay' as IcePolicy };
                      setLocalConfig(newConfig);
                      onConfigChange(newConfig);
                      eventBus.emit('ui:toast', { message: 'TCP中转已自动切换为仅中转模式，正在重连...', type: 'info' });
                      webRTCService.reconnectWithNewConfig({ turnProtocol: newProtocol, icePolicy: 'relay' });
                    } else {
                      updateConfig('turnProtocol', newProtocol);
                      eventBus.emit('ui:toast', { message: 'TURN协议已更新，正在重连...', type: 'info' });
                      webRTCService.reconnectWithNewConfig({ turnProtocol: newProtocol });
                    }
                  }}
                />
                <div className="text-[10px] text-zinc-500 -mt-2">
                  自动：同时竞速UDP和TCP中转，选择最优
                </div>
              </>
            )}
          </Section>

          {/* Video Settings */}
          <Section icon={<Video size={16} />} title="视频与串流">
            {/* 分辨率选择器 - 仅在服务端提供可用分辨率时显示；只读模式下隐藏 */}
            {!viewOnly && availableResolutions && availableResolutions.length > 0 && (() => {
              const resList = availableResolutions.split(',').filter(Boolean);
              if (resList.length === 0) return null;
              return (
                <div className="space-y-2">
                  <div className="text-xs text-zinc-500">远程分辨率</div>
                  <select
                    value={currentResolution && resList.includes(currentResolution) ? currentResolution : '0x0'}
                    onChange={(e) => {
                      const v = e.target.value;
                      const [w, h] = v.split('x').map(Number);
                      webRTCService.sendResolutionConfig(w || 0, h || 0);
                      eventBus.emit('ui:toast', {
                        message: v === '0x0' ? '正在恢复原始分辨率...' : `正在切换分辨率到 ${v}...`,
                        type: 'info'
                      });
                    }}
                    className="w-full bg-zinc-800 text-zinc-300 text-sm rounded-lg px-3 py-2 border border-zinc-700 focus:border-blue-500 focus:outline-none"
                  >
                    <option value="0x0">原始分辨率</option>
                    {resList.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  <div className="text-[10px] text-zinc-500 -mt-1">
                    当前: {currentResolution || '未知'}
                  </div>
                </div>
              );
            })()}
            {/* 编码器选择（阶段 2：catalog-driven，删除旧 6 分支 if-else）
                数据源：host server_encoder_catalog 消息 → StatsService 合并到 ServerStats */}
            <EncoderCatalogSection serverStats={serverStats} />
          </Section>

          {/* Screen Capturer（host 启动期 benchmark，全局生效） */}
          {screenCapturer && (
            <Section icon={<Camera size={16} />} title="截图器（实验）">
              <div className="text-[10px] text-zinc-500 -mt-2 mb-1">
                Host 启动时自动测速选优；切换会触发 host 重建采集器。
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-zinc-500">当前生效</span>
                <span className="text-xs font-mono text-blue-400 uppercase">
                  {screenCapturer.active}
                  {screenCapturer.userOverride && <span className="ml-2 text-yellow-400">[手动]</span>}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-zinc-500">支持的截图器</span>
                <span className="text-[10px] font-mono text-zinc-400">
                  {screenCapturer.supportedOnThisOs.join(' / ')}
                </span>
              </div>

              {/* 下拉：auto / wgc / dxgi（按 supportedOnThisOs 限制可选项）*/}
              <div className="space-y-1">
                <span className="text-xs text-zinc-500">手动覆盖</span>
                <select
                  className="w-full bg-zinc-900 border border-zinc-700 text-zinc-200 text-xs rounded px-2 py-1 focus:outline-none focus:border-blue-500"
                  value={screenCapturer.userOverride ? screenCapturer.preferred : 'auto-managed'}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === 'auto-managed') {
                      // 用户撤销覆盖：userOverride=false，host 端会忽略 preferred 参数自动取 benchmark winner
                      webRTCService.sendScreenCapturerOverride(false, screenCapturer.active);
                    } else {
                      webRTCService.sendScreenCapturerOverride(true, v as 'auto' | 'wgc' | 'dxgi' | 'gdi');
                    }
                  }}
                >
                  <option value="auto-managed">自动（按 benchmark 选）</option>
                  <option value="auto">手动：auto（让 libwebrtc 自决）</option>
                  {screenCapturer.supportedOnThisOs.includes('wgc') && (
                    <option value="wgc">手动：强制 WGC</option>
                  )}
                  {screenCapturer.supportedOnThisOs.includes('dxgi') && (
                    <option value="dxgi">手动：强制 DXGI</option>
                  )}
                  {screenCapturer.supportedOnThisOs.includes('gdi') && (
                    <option value="gdi">手动：强制 GDI</option>
                  )}
                </select>
              </div>

              {/* benchmark 数值表 */}
              {screenCapturer.lastBenchmark && (
                <div className="bg-zinc-900/50 border border-zinc-800 rounded p-2 space-y-1">
                  <div className="text-[10px] text-zinc-500 mb-1">
                    上次 benchmark · {screenCapturer.lastBenchmark.os} · {screenCapturer.lastBenchmark.displaySignature}
                  </div>
                  {(['wgc', 'dxgi', 'gdi'] as const).map((k) => {
                    const r = screenCapturer.lastBenchmark![k];
                    // 旧 host 写的 lastBenchmark 没有 gdi 字段 → r 为 undefined，跳过
                    if (!r || !r.tested) return null;
                    const isWinner = screenCapturer.lastBenchmark!.winner === k;
                    return (
                      <div key={k} className="flex justify-between items-center text-[11px] font-mono">
                        <span className={`uppercase ${isWinner ? 'text-green-400' : 'text-zinc-400'}`}>
                          {k}{isWinner ? ' ★' : ''}
                        </span>
                        <span className={r.available ? 'text-zinc-300' : 'text-red-400'}>
                          {r.available
                            ? `${r.avgMs.toFixed(2)}ms · ${r.successCount}/${r.totalCount} · ${r.frameWidth}x${r.frameHeight}`
                            : `不可用 (${r.successCount}/${r.totalCount}${r.note ? ' · ' + r.note : ''})`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* 重新检测按钮 */}
              <button
                disabled={rebenchPending}
                onClick={() => {
                  setRebenchPending(true);
                  webRTCService.sendScreenCapturerRebench();
                }}
                className={`w-full flex items-center justify-center gap-2 text-xs px-3 py-1.5 rounded border transition-colors ${
                  rebenchPending
                    ? 'border-zinc-800 text-zinc-600 cursor-not-allowed'
                    : 'border-zinc-700 text-zinc-300 hover:border-blue-600 hover:text-blue-400'
                }`}
              >
                <RefreshCw size={12} className={rebenchPending ? 'animate-spin' : ''} />
                {rebenchPending ? '正在重新检测…' : '重新检测截图器'}
              </button>
            </Section>
          )}

          {/* 内网 TURN */}
          <Section icon={<Network size={16} />} title="内网 TURN">
            {/* 状态行 */}
            <div className="flex items-center justify-between text-xs">
              <span className="text-zinc-500">状态</span>
              <span className="text-zinc-300">
                {(() => {
                  if (!turnStatus) return '加载中…';
                  if (!turnStatus.enabled) return '未启用（host_config.json turnDiscovery.enabled=false）';
                  if (!turnStatus.discoveryComplete) return '发现中…';
                  const reachable = turnStatus.endpoints.filter(e => e.reachable).length;
                  const total = turnStatus.endpoints.length;
                  if (turnStatus.errorReason === 'ini-missing') return '失败：找不到 Config.ini';
                  if (turnStatus.errorReason === 'port-missing') return '失败：Config.ini 缺 [local] http_port';
                  if (turnStatus.errorReason === 'fetch-failed') return '失败：调内网 IP 接口失败';
                  if (turnStatus.errorReason === 'no-ips') return '失败：接口未返回 IP';
                  if (turnStatus.errorReason === 'probe-all-fail') return '失败：所有端点不可达';
                  return `${reachable} / ${total} 个端点可用`;
                })()}
              </span>
            </div>

            {/* Endpoint 列表 */}
            {turnStatus && turnStatus.endpoints.length > 0 && (
              <div className="space-y-1.5">
                {turnStatus.endpoints.map((ep: InternalTurnEndpoint) => {
                  const key = `${ep.ip}:${ep.port}`;
                  const probing = turnProbingAll || turnProbingKey === key;
                  return (
                    <div key={key} className="bg-white/5 rounded">
                      <div className="flex items-center justify-between text-xs px-2 py-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={ep.reachable ? 'text-green-400' : 'text-red-400'}>
                            {ep.reachable ? '✓' : '✗'}
                          </span>
                          <span className="text-zinc-300 font-mono truncate">{key}</span>
                          <span className="text-zinc-500">
                            {ep.protocols.length > 0 ? ep.protocols.join('+').toUpperCase() : '—'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={ep.reachable ? 'text-zinc-300' : 'text-zinc-600'}>
                            {ep.reachable
                              ? (ep.rttMs > 0 ? `${ep.rttMs}ms` : ep.rttMs === 0 ? '<1ms' : '—')
                              : '—'}
                          </span>
                          <button
                            disabled={probing}
                            onClick={() => {
                              setTurnProbingKey(key);
                              webRTCService.probeInternalTurn([{ ip: ep.ip, port: ep.port }]);
                            }}
                            className={`px-2 py-0.5 rounded border text-[10px] transition-colors ${
                              probing
                                ? 'border-zinc-800 text-zinc-600 cursor-not-allowed'
                                : 'border-zinc-700 text-zinc-400 hover:border-blue-600 hover:text-blue-400'
                            }`}
                          >
                            <RefreshCw size={10} className={`inline ${probing ? 'animate-spin' : ''}`} />
                          </button>
                        </div>
                      </div>
                      {/* 端到端深度探测（「全部测速」）：直连 vs 经内网转发两条路对比 */}
                      {ep.deep?.measured && (
                        <div className="px-2 pb-1.5 pt-1 space-y-0.5 border-t border-white/5 text-[10px]">
                          {[
                            { label: `直连 ${ep.deep.turnHost}`, p: ep.deep.direct },
                            { label: '转发(内网)', p: ep.deep.forward },
                          ].map(({ label, p }) => (
                            <div key={label} className="flex items-center justify-between">
                              <span className="text-zinc-500 truncate">{label}</span>
                              <span className={p.ok ? 'text-zinc-300 shrink-0' : 'text-red-400 shrink-0'}>
                                {p.ok
                                  ? `✓ ${p.rttMs > 0 ? `${p.rttMs}ms` : p.rttMs === 0 ? '<1ms' : '—'}${p.lossPct >= 0 ? ` 丢${p.lossPct}%` : ''}`
                                  : '✗ 不可达'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* 全部测速 / 重新发现 */}
            <div className="flex flex-col gap-2">
              <button
                disabled={turnProbingAll || turnRediscovering || !turnStatus?.enabled}
                onClick={() => {
                  setTurnProbingAll(true);
                  webRTCService.probeInternalTurn([]);
                }}
                className={`w-full flex items-center justify-center gap-2 text-xs px-3 py-1.5 rounded border transition-colors ${
                  turnProbingAll || turnRediscovering || !turnStatus?.enabled
                    ? 'border-zinc-800 text-zinc-600 cursor-not-allowed'
                    : 'border-zinc-700 text-zinc-300 hover:border-blue-600 hover:text-blue-400'
                }`}
              >
                <RefreshCw size={12} className={turnProbingAll ? 'animate-spin' : ''} />
                {turnProbingAll ? '测速中…' : '全部测速'}
              </button>
              <button
                disabled={turnRediscovering || turnProbingAll || !turnStatus?.enabled}
                onClick={() => {
                  setTurnRediscovering(true);
                  webRTCService.rediscoverInternalTurn();
                }}
                className={`w-full flex items-center justify-center gap-2 text-xs px-3 py-1.5 rounded border transition-colors ${
                  turnRediscovering || turnProbingAll || !turnStatus?.enabled
                    ? 'border-zinc-800 text-zinc-600 cursor-not-allowed'
                    : 'border-zinc-700 text-zinc-300 hover:border-blue-600 hover:text-blue-400'
                }`}
                title="重新调用内网接口拉取最新 IP 列表，并重新探测"
              >
                <Network size={12} className={turnRediscovering ? 'animate-spin' : ''} />
                {turnRediscovering ? '发现中…' : '发现内网 TURN'}
              </button>
            </div>

            {/* 仅使用内网 TURN 开关 */}
            <Toggle
              label="仅使用内网 TURN（测试用）"
              description="开启后 host 端会忽略前端的公网 TURN，仅竞速内网 forwarder；若内网 TURN 不可用，连接将失败。切换后需要重连生效。"
              active={localConfig.hostInternalTurnOnly}
              onToggle={() => {
                const next = !localConfig.hostInternalTurnOnly;
                updateConfig('hostInternalTurnOnly', next);
                eventBus.emit('ui:toast', { message: '内网 TURN 模式已切换，正在重连…', type: 'info' });
                webRTCService.reconnectWithNewConfig({ hostInternalTurnOnly: next });
              }}
              disabled={!turnStatus?.enabled}
              disabledReason="host 端未启用内网 TURN 发现"
            />
          </Section>

          {/* Bitrate Control（实验性，全局生效） */}
          <Section icon={<Video size={16} />} title="码率控制（实验）">
            <div className="text-[10px] text-red-400 -mt-2 mb-2">
              ⚠ 此设置全局生效，会影响连接到此 host 的所有客户端
            </div>
            <Toggle
              label="固定码率模式"
              description={localConfig.fixedBitrateEnabled
                ? '编码器锁定固定码率，关闭 BWE 自适应（适合稳定且 ≥10Mbps 的链路）'
                : '默认：跟随 libwebrtc BWE 动态调整'}
              active={localConfig.fixedBitrateEnabled}
              onToggle={() => {
                const next = !localConfig.fixedBitrateEnabled;
                const newConfig = { ...localConfig, fixedBitrateEnabled: next };
                setLocalConfig(newConfig);
                onConfigChange(newConfig);
                webRTCService.sendFixedBitrateConfig(
                  next,
                  newConfig.fixedBitrateNvencKbps,
                  newConfig.fixedBitrateSwKbps);
                eventBus.emit('ui:toast', { message: `固定码率模式已${next ? '开启' : '关闭'}`, type: 'success' });
              }}
            />
            {localConfig.fixedBitrateEnabled && (
              <>
                <Range
                  label="NVENC 固定码率"
                  value={localConfig.fixedBitrateNvencKbps}
                  min={1000} max={20000} step={500} unit="kbps"
                  onChange={(v) => updateConfig('fixedBitrateNvencKbps', v)}
                  onChangeEnd={(v) => {
                    webRTCService.sendFixedBitrateConfig(true, v, localConfig.fixedBitrateSwKbps);
                    eventBus.emit('ui:toast', { message: `NVENC 固定码率已设为 ${v} kbps`, type: 'success' });
                  }}
                />
                <Range
                  label="软编 固定码率"
                  value={localConfig.fixedBitrateSwKbps}
                  min={500} max={4000} step={100} unit="kbps"
                  onChange={(v) => updateConfig('fixedBitrateSwKbps', v)}
                  onChangeEnd={(v) => {
                    webRTCService.sendFixedBitrateConfig(true, localConfig.fixedBitrateNvencKbps, v);
                    eventBus.emit('ui:toast', { message: `软编 固定码率已设为 ${v} kbps`, type: 'success' });
                  }}
                />
              </>
            )}
          </Section>

          {/* Input Settings */}
          <Section icon={<MousePointer size={16} />} title="输入行为">
            <Toggle
              label="游戏鼠标模式"
              description="隐藏本地光标，使用相对移动（Alt+F1 退出）"
              active={localConfig.gameMouseMode}
              onToggle={() => {
                const newValue = !localConfig.gameMouseMode;
                updateConfig('gameMouseMode', newValue);
                eventBus.emit('input:gameMouseMode', newValue);
              }}
            />
            <Toggle
              label="剪贴板同步"
              description="自动同步本地和远程剪贴板"
              active={localConfig.clipboardSync}
              onToggle={() => updateConfig('clipboardSync', !localConfig.clipboardSync)}
            />
          </Section>

          {/* Audio Settings */}
          <Section icon={<Volume2 size={16} />} title="音频设置">
            <Toggle
              label="系统声音"
              description="播放远程桌面的系统音频"
              active={localConfig.systemAudioEnabled}
              onToggle={() => {
                const newValue = !localConfig.systemAudioEnabled;
                updateConfig('systemAudioEnabled', newValue);
                webRTCService.sendAudioConfig(newValue, localConfig.micAudioEnabled);
              }}
            />
            <Toggle
              label="远程麦克风"
              description="播放远程主机的麦克风输入"
              active={localConfig.micAudioEnabled}
              onToggle={() => {
                const newValue = !localConfig.micAudioEnabled;
                updateConfig('micAudioEnabled', newValue);
                webRTCService.sendAudioConfig(localConfig.systemAudioEnabled, newValue);
              }}
              disabled={true}
              disabledReason="暂不支持，待后续版本实现"
            />
          </Section>

          {/* Display Settings */}
          <Section icon={<Monitor size={16} />} title="显示设置">
            <Select
              label="顶部状态栏 (F8 切换)"
              value={localConfig.statusBarVisibility}
              options={[
                { value: 'auto', label: '自动' },
                { value: 'on', label: '始终显示' },
                { value: 'off', label: '始终隐藏' },
              ]}
              onChange={(v) => updateConfig('statusBarVisibility', v as StatusBarVisibility)}
            />
            <div className="text-[10px] text-zinc-500 -mt-2">
              {localConfig.statusBarVisibility === 'auto'
                ? '连接后显示1分钟，之后自动隐藏（网络波动时重新显示）'
                : localConfig.statusBarVisibility === 'on'
                  ? '始终显示顶部状态栏'
                  : '始终隐藏顶部状态栏'}
            </div>
            <Select
              label="状态栏方向"
              value={localConfig.statusBarOrientation}
              options={[
                { value: 'vertical', label: '竖向' },
                { value: 'horizontal', label: '横向' },
              ]}
              onChange={(v) => updateConfig('statusBarOrientation', v as StatusBarOrientation)}
            />
          </Section>

          {/* Debug - Crash Test */}
          <Section icon={<AlertTriangle size={16} />} title="调试 - 崩溃测试">
            <div className="text-[10px] text-red-400 mb-2">
              警告：此按钮会导致 Host 程序崩溃，仅用于测试崩溃日志系统
            </div>
            <button
              onClick={() => {
                if (confirm('确定要触发 C++ Crash 吗？Host 将会崩溃！')) {
                  signalingService.sendCrashTest();
                }
              }}
              className="w-full py-2 px-3 bg-red-600/30 hover:bg-red-600/50 text-red-300 rounded-lg text-xs font-medium transition-all border border-red-500/30"
            >
              C++ Crash Test
            </button>
          </Section>
        </div>

        {/* Footer - Reset only */}
        <div className="p-6 border-t border-white/5 bg-white/5">
          <button
            onClick={resetToDefaults}
            className="w-full py-3 px-4 bg-white/10 hover:bg-white/20 text-white rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2"
          >
            <RotateCcw size={16} />
            恢复默认
          </button>
        </div>
      </div>
    </>
  );
};

export default React.memo(SettingsPanel);
